import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getClientStatus, isDispatchedStatusOverride } from './service_request_status';
import { formatClientNotes } from './client_notes';

const fieldValue = (record, fieldId) => record?.[fieldId]?.value ?? '';
const formatValue = value => {
  if (!value) return '—';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return [value.street, value.street2, value.city, value.region, value.postal, value.country].filter(Boolean).join(', ') || '—';
};
const formatDate = value => {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const ServiceTicket = () => {
  const dispatch = useDispatch();
  const routeLocation = useLocation();
  const params = useParams();
  const id = routeLocation.state?.id || params.id;
  const portalApp = new URLSearchParams(routeLocation.search).get('app') || 'waste';
  const isFire = portalApp === 'fire_pest';
  const [ticket, setTicket] = useState(null);
  const [notesUpdate, setNotesUpdate] = useState('');
  const [loadState, setLoadState] = useState({ status: 'loading', message: '' });
  const [saveState, setSaveState] = useState({ status: 'idle', message: '' });

  const logoutUser = useCallback(() => {
    dispatch({ type: 'LOG_OUT', payload: null });
    sessionStorage.removeItem('currentUser');
    window.location = '/';
  }, [dispatch]);

  const fetchTicket = useCallback(async () => {
    setLoadState({ status: 'loading', message: '' });
    try {
      const response = await fetch(`/apiserviceticket/${id}?app=${portalApp}`);
      const data = await response.json().catch(() => ({}));
      if (response.status === 401 || data?.sessionExpired) return logoutUser();
      if (!response.ok) throw new Error(data?.message || 'Unable to load this service request.');
      setTicket(data);
      setLoadState({ status: 'ready', message: '' });
    } catch (error) {
      setLoadState({ status: 'error', message: error.message || 'Unable to load this service request.' });
    }
  }, [id, logoutUser, portalApp]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  const saveNote = async () => {
    const newNote = notesUpdate.trim();
    if (!newNote) return;
    setSaveState({ status: 'saving', message: '' });
    try {
      const requestedBy = fieldValue(ticket, 18);
      const response = await fetch('/apiupdateServiceRequest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recordId: fieldValue(ticket, 3), notes: requestedBy ? `${requestedBy}: ${newNote}` : newNote, portalApp })
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401 || data?.sessionExpired) return logoutUser();
      if (!response.ok) throw new Error(data?.message || 'Unable to save your note.');
      const updatedNotes = data?.[0]?.[26]?.value ?? data?.[0]?.[567]?.value ?? newNote;
      setTicket(current => ({ ...current, 26: { value: updatedNotes } }));
      setNotesUpdate('');
      setSaveState({ status: 'success', message: 'Note saved successfully.' });
    } catch (error) {
      setSaveState({ status: 'error', message: error.message || 'Unable to save your note.' });
    }
  };

  const rawStatus = isFire && fieldValue(ticket, 38)
    ? 'Complete'
    : isFire && isDispatchedStatusOverride(fieldValue(ticket, 192))
    ? 'Dispatched'
    : fieldValue(ticket, '_statusText') || (fieldValue(ticket, 21) ? 'Complete' : 'Pending');
  const status = getClientStatus(rawStatus);
  const details = [
    ['Location code', fieldValue(ticket, 10)],
    ['Address', formatValue(fieldValue(ticket, 15))],
    [isFire ? 'Trade' : 'Issue type', fieldValue(ticket, 7)],
    ...(isFire
      ? [['Request type', fieldValue(ticket, 111)], ['Inspection date', formatDate(fieldValue(ticket, 38))]]
      : [['Requested by', fieldValue(ticket, 18)], ['Client region', fieldValue(ticket, 27)]])
  ];

  return (
    <div className="Ticket detailPage">
      {loadState.status === 'loading' && <div className="detailState" role="status">Loading service request…</div>}
      {loadState.status === 'error' && <div className="detailState detailStateError" role="alert">{loadState.message}</div>}
      {ticket && <>
        <header className="detailHero">
          <div><span className="detailEyebrow">Service request</span><h2>Request #{fieldValue(ticket, 3)}</h2><p>Review the request details and add information for the Facilities USA service team.</p></div>
          <span className={`detailStatus detailStatus-${status.tone}`}><i aria-hidden="true" />{status.label}</span>
        </header>

        <section className="detailCard" aria-labelledby="request-details-heading">
          <div className="detailCardHeader"><div><h3 id="request-details-heading">Request details</h3><p>The latest information currently available for this request.</p></div><span className="detailAppBadge">{isFire ? 'Inspections & Maintenance' : 'Waste'}</span></div>
          <div className="detailGrid">
            {details.map(([label, value]) => <div className="detailField" key={label}><span>{label}</span><strong>{formatValue(value)}</strong></div>)}
            <div className="detailField detailFieldFull"><span>Notes</span><div className="detailNotes">{formatValue(formatClientNotes(fieldValue(ticket, 26)))}</div></div>
          </div>
        </section>

        <section className="detailCard detailNoteCard" aria-labelledby="add-note-heading">
          <div className="detailCardHeader"><div><h3 id="add-note-heading">Add a note</h3><p>Share an update, access instructions, or additional details with the service team.</p></div></div>
          <div className="detailNoteBody">
            <textarea aria-label="Add a note" value={notesUpdate} onChange={event => { setNotesUpdate(event.target.value); setSaveState({ status: 'idle', message: '' }); }} placeholder="Type your note here…" rows="5" maxLength="2000" />
            <div className="detailNoteFooter"><span className={saveState.status === 'error' ? 'detailMessageError' : 'detailMessage'} role={saveState.message ? 'status' : undefined}>{saveState.message}</span><button type="button" className="mainButton detailSaveButton" disabled={!notesUpdate.trim() || saveState.status === 'saving'} onClick={saveNote}>{saveState.status === 'saving' ? 'Saving…' : 'Save note'}</button></div>
          </div>
        </section>
      </>}
    </div>
  );
};
